import { createContext, useState, useContext, useCallback, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

import { AuthContextType, ISession } from "../interfaces/auth";
import { RoleLevel } from "./roles";

const initialAuthContext: AuthContextType = {
  isAuthenticated: false,
  isRedirecting: false,
  user: null,
  signin: async () => { },
  signout: () => { },
  homepage: '/',
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthContext = createContext(initialAuthContext);

export const useAuth = (): AuthContextType => useContext(AuthContext);

const getHomepage = (role: RoleLevel): string => {
  switch (role) {
    case RoleLevel.SISADM:
      return '/admin/dashboard'
    case RoleLevel.ADMIN:
      return '/admin/dashboard'
    case RoleLevel.PARTNER:
      return '/assessments/dashboard'
    case RoleLevel.ANALYST:
      return '/assessments/all'
    default:
      return '/';
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<ISession | null>(null);
  const [homepage, setHomepage] = useState<string>('/');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();

  const decodeToken = useCallback((token: string) => {
    const decodedToken: ISession = jwtDecode(token);
    return decodedToken;
  }, []);

  const accessControlRedirect = useCallback((role: RoleLevel) => {
    navigate(getHomepage(role));
  }, []);

  const signin = async (auth: string, withRedirect: boolean = true) => {
    const decodedToken: ISession = decodeToken(auth);
    localStorage.setItem("token", auth);
    setIsAuthenticated(true);
    setIsRedirecting(false);
    setUser(decodedToken);
    setHomepage(getHomepage(decodedToken.permission));
    if (withRedirect) {
      accessControlRedirect(decodedToken.permission);
    }
  };

  const signout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    setUser(null);
    setHomepage('/');
    navigate('/');
    window.location.reload();
  };

  const checkSession = useCallback((session: ISession) => {
    const expirationTime = (session.exp || 0) * 1000;
    const now = new Date().getTime();
    return now > expirationTime;
  }, []);

  const shouldRedirectToDifferentApp = useCallback((session: ISession): boolean => {
    const currentUrl = window.location.href;
    const isAdminApp = currentUrl.includes('localhost:3002') || currentUrl.includes('backoffice');
    const isClientApp = currentUrl.includes('localhost:3003') || currentUrl.includes('app');

    if (isRedirecting) {
      return false;
    }

    return (
      ([RoleLevel.ADMIN, RoleLevel.SISADM].includes(session.permission) && isClientApp) ||
      ([RoleLevel.PARTNER, RoleLevel.ANALYST].includes(session.permission) && isAdminApp)
    );
  }, []);

  const getRedirectUrl = useCallback((session: ISession): string => {
    const isAdminUser = session.permission === RoleLevel.ADMIN || session.permission === RoleLevel.SISADM;
    const isLocalhost = window.location.hostname === 'localhost';

    if (isAdminUser) {
      return isLocalhost ? 'http://localhost:3002/callback' : 'https://backoffice.bemap.com.br/callback';
    } else {
      return isLocalhost ? 'http://localhost:3003/callback' : 'https://app.bemap.com.br/callback';
    }
  }, []);

  const handleCrossAppRedirect = useCallback((session: ISession) => {
    if (!shouldRedirectToDifferentApp(session)) {
      return false;
    }

    const redirectUrl = getRedirectUrl(session);
    const token = localStorage.getItem('token');

    if (token && !isRedirecting) {
      localStorage.removeItem('token');
      setIsRedirecting(true);
      window.location.href = `${redirectUrl}?btoken=${encodeURIComponent(token)}`;
      return true;
    }

    return false;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const urlParams = new URLSearchParams(window.location.search);
    const btoken = urlParams.get('btoken');

    if (btoken) {
      localStorage.removeItem('token');
      signin(btoken, false);
      return;
    }

    if (token) {
      const userFromToken = decodeToken(token);
      try {
        if (checkSession(userFromToken)) {
          signout();
          return
        }
        handleCrossAppRedirect(userFromToken);
        signin(token, false);
      } catch (error) {
        signout();
      }
    }
  }, [isRedirecting, navigate]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        signin,
        signout,
        homepage,
        isRedirecting
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
